import React, { useState, useEffect, useRef, useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import { AuthContext } from '../context/AuthContext';

const ChatWindow = ({ activeChat, messages, onSendMessage, onInitiateCall, onBack }) => {
  const { theme } = useContext(ThemeContext);
  const { user: currentUser } = useContext(AuthContext);
  const [typedMessage, setTypedMessage] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (!activeChat) {
    return (
      <div className={`flex-1 flex flex-col items-center justify-center p-6 text-center h-full ${theme === 'dark' ? 'bg-[#0B0F19]' : 'bg-gray-50'}`}>
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-tr from-accentBlue to-accentViolet flex items-center justify-center text-white text-3xl font-black mb-4 shadow-xl opacity-80">
          V
        </div>
        <h3 className="text-xl font-bold tracking-wide">No Active Conversation</h3>
        <p className="text-gray-400 text-sm max-w-sm mt-1">Select a participant from recent records or trace their identity through direct query matching infrastructure.</p>
      </div>
    );
  }

  const handleSendSubmit = (e) => {
    e.preventDefault();
    if (!typedMessage.trim()) return;
    onSendMessage(typedMessage);
    setTypedMessage('');
  };

  return (
    <div className={`flex-1 flex flex-col h-full w-full ${theme === 'dark' ? 'bg-[#0B0F19]' : 'bg-gray-50'}`}>
      <div className={`h-16 px-4 sm:px-6 border-b flex items-center justify-between shadow-sm z-10 ${theme === 'dark' ? 'bg-[#151D30] border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {/* Mobile Back Button Controls */}
          <button 
            onClick={onBack}
            className={`p-2 rounded-lg mr-1 md:hidden transition-colors ${theme === 'dark' ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>

          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-white uppercase text-sm flex-shrink-0" style={{ backgroundColor: activeChat.user.avatar.startsWith('#') ? activeChat.user.avatar : '#3B82F6' }}>
            {activeChat.user.username.charAt(0)}
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-sm tracking-wide truncate">{activeChat.user.username}</h3>
            <p className="text-xs text-gray-400 truncate hidden sm:block">{activeChat.user.email}</p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <button 
            onClick={() => onInitiateCall('voice')}
            className={`p-2 sm:p-2.5 rounded-xl transition-all ${theme === 'dark' ? 'bg-[#1C263B] text-blue-400 hover:bg-blue-600 hover:text-white' : 'bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white'} shadow-sm`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.72.55.55 0 00.1.24l1.23 3.29a1 1 0 01-.28 1l-1.6 1.6a15.56 15.56 0 006.11 6.11l1.6-1.6a1 1 0 011-.28l3.29 1.23c.13.04.25.12.34.24a1 1 0 01.72.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
          </button>
          <button 
            onClick={() => onInitiateCall('video')}
            className={`p-2 sm:p-2.5 rounded-xl transition-all ${theme === 'dark' ? 'bg-[#1C263B] text-purple-400 hover:bg-purple-600 hover:text-white' : 'bg-purple-50 text-purple-600 hover:bg-purple-600 hover:text-white'} shadow-sm`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
        {messages.map((msg, index) => {
          const isMe = msg.sender === currentUser.id;
          return (
            <div key={msg._id || index} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] sm:max-w-[70%] px-4 py-2.5 rounded-2xl text-sm shadow-sm transition-all ${
                isMe 
                  ? 'bg-gradient-to-br from-accentBlue to-blue-700 text-white rounded-tr-none' 
                  : (theme === 'dark' ? 'bg-[#151D30] text-gray-100 rounded-tl-none border border-gray-800' : 'bg-white text-gray-800 rounded-tl-none border border-gray-100')
              }`}>
                <p className="leading-relaxed break-words">{msg.text}</p>
                <div className="text-[10px] mt-1 text-right block opacity-60">
                  {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSendSubmit} className={`p-4 border-t ${theme === 'dark' ? 'bg-[#151D30] border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Type your message..."
            value={typedMessage}
            onChange={(e) => setTypedMessage(e.target.value)}
            className={`flex-1 px-4 py-2.5 rounded-xl text-sm outline-none border transition-all ${
              theme === 'dark' 
                ? 'bg-[#0B0F19] border-gray-700 text-white focus:border-accentBlue' 
                : 'bg-gray-100 border-gray-200 text-gray-900 focus:border-accentBlue'
            }`}
          />
          <button
            type="submit"
            className="px-4 sm:px-5 py-2.5 bg-accentBlue hover:bg-blue-600 text-white rounded-xl font-medium text-sm transition-all shadow-md flex items-center justify-center flex-shrink-0"
          >
            <svg className="w-4 h-4 transform rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatWindow;